import { z } from 'zod';

import { ExperienceVersion } from '../../../../experience/const';
import { buildQueryString } from '../../../../utils/querystring';
import { LanguageCode, ProductLine } from '../../../brand/brand';
import {
  makeEndpoint,
  paginateQuerySchema,
  paginateResponseSchema,
  sortOptions,
  stringBooleanSchema,
} from '../../../common';
import { BookingAvailabilityModeValue } from '../../../experience/experiences';
import { ListingKind } from '../../listing';
import {
  CategorySchemaV3,
  HostedBySchemaV3,
  ListingsV3SortBy,
  MediasSchemaV3,
  PointsEligibleSchemaV3,
  PromotedListingSchemaV3,
  ResourceGroupBaseSchemaV3,
  VibesSchemaV3,
} from '../common';

export const GetListingsV3FeaturedPublicQuerySchema = z.object({
  isUnlisted: stringBooleanSchema.default(false),
  language: z.nativeEnum(LanguageCode).default(LanguageCode.EN_US),
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.PUBLISHED),
});

export const GetListingsV3FeaturedPublicResponseSchema = z.array(
  z.object({
    advertisedPrice: z.number().optional().nullable(),
    bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).optional().nullable(),
    category: CategorySchemaV3.optional().nullable(),
    eventDates: z.array(z.date()).optional().nullable(),
    experienceId: z.string().uuid().optional().nullable(),
    hidePrice: z.boolean().optional().nullable(),
    hostedBy: HostedBySchemaV3.optional().nullable(),
    id: z.string().uuid(),
    isExclusive: z.boolean(),
    isUnlisted: z.boolean(),
    kind: z.nativeEnum(ListingKind),
    maxParticipants: z.number().optional().nullable(),
    medias: z.array(MediasSchemaV3),
    order: z.number().optional().nullable(),
    pointsEligible: PointsEligibleSchemaV3.optional().nullable(),
    pricePerType: z.string().optional().nullable(),
    productLine: z.nativeEnum(ProductLine),
    rating: z.number().optional().nullable(),
    resourceGroupCollectionId: z.string().uuid().optional().nullable(),
    slug: z.string().optional().nullable(),
    startingPrice: z.number().optional().nullable(),
    summary: z.string().optional().nullable(),
    title: z.string().optional().nullable(),
    vibes: z.array(VibesSchemaV3),
  }),
);

export type GetListingsV3FeaturedPublicResponse = z.infer<typeof GetListingsV3FeaturedPublicResponseSchema>;

export const GetListingsV3FeaturedPublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof GetListingsV3FeaturedPublicQuerySchema> }) =>
    `v3/listings/featured?${buildQueryString(query)}`,
  responseSchema: GetListingsV3FeaturedPublicResponseSchema,
  querySchema: GetListingsV3FeaturedPublicQuerySchema,
  requestSchema: z.void(),
});

export const GetListingsV3OverviewPublicQuerySchema = z.object({
  isUnlisted: stringBooleanSchema.default(false),
});

const StatusCountsPublicObjectSchema = z.object({
  count: z.number(),
  media: MediasSchemaV3.optional().nullable(),
});

const StatusCountsPublicSchema = z.object({
  activate: StatusCountsPublicObjectSchema,
  host: StatusCountsPublicObjectSchema,
  reserve: StatusCountsPublicObjectSchema,
});

export const GetListingsV3OverviewPublicResponseSchema = z.object({
  brandOwned: StatusCountsPublicSchema,
  promoted: StatusCountsPublicSchema,
});

export const GetListingsV3OverviewPublicEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `v3/listings/overview`,
  responseSchema: GetListingsV3OverviewPublicResponseSchema,
  requestSchema: z.void(),
  querySchema: GetListingsV3OverviewPublicQuerySchema,
});

export const ListingsV3PublicSortByOptions = [
  ListingsV3SortBy.CATEGORY,
  ListingsV3SortBy.CREATED_AT,
  ListingsV3SortBy.STATUS,
  ListingsV3SortBy.TITLE,
  ListingsV3SortBy.LISTING_ORDER,
] as const satisfies ReadonlyArray<ListingsV3SortBy>;

export const GetListingsV3PublicQuerySchema = paginateQuerySchema({}).extend({
  categoryIds: z.array(z.string().uuid()).optional(),
  isUnlisted: stringBooleanSchema.default(false),
  language: z.nativeEnum(LanguageCode).default(LanguageCode.EN_US).optional(),
  productLines: z.array(z.nativeEnum(ProductLine)).optional(),
  sortBy: z.enum(ListingsV3PublicSortByOptions).optional(),
  sortDir: z.enum(sortOptions).optional(),
  vibeIds: z.array(z.string().uuid()).optional(),
});

export const GetListingsV3PublicResponseSchema = paginateResponseSchema(
  z.object({
    advertisedPrice: z.number().optional().nullable(),
    bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).optional().nullable(),
    brandId: z.string().uuid().optional().nullable(),
    category: CategorySchemaV3.optional().nullable(),
    eventDates: z.array(z.date()).optional().nullable(),
    experienceId: z.string().uuid().optional().nullable(),
    hidePrice: z.boolean().optional().nullable(),
    hostedBy: HostedBySchemaV3.optional().nullable(),
    id: z.string().uuid(),
    isExclusive: z.boolean(),
    kind: z.nativeEnum(ListingKind),
    maxParticipants: z.number().optional().nullable(),
    medias: z.array(MediasSchemaV3),
    pointsEligible: PointsEligibleSchemaV3.optional().nullable(),
    pricePerType: z.string().optional().nullable(),
    productLine: z.nativeEnum(ProductLine),
    promotedListing: PromotedListingSchemaV3.optional().nullable(),
    rating: z.number().optional().nullable(),
    resourceGroupCollectionId: z.string().uuid().optional().nullable(),
    resourceGroups: z.array(ResourceGroupBaseSchemaV3),
    slug: z.string().optional().nullable(),
    startingPrice: z.number().optional().nullable(),
    summary: z.string().optional().nullable(),
    title: z.string().optional().nullable(),
    vibes: z.array(VibesSchemaV3),
  }),
);

export const GetListingsV3PublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof GetListingsV3PublicQuerySchema> }) => `v3/listings?${buildQueryString(query)}`,
  responseSchema: GetListingsV3PublicResponseSchema,
  querySchema: GetListingsV3PublicQuerySchema,
  requestSchema: z.void(),
});
